import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { ConsentService } from '@navfleet/client/cookie-consent/domain/data-access';
import { DateService } from '@navfleet/client/core/date';
import { LayoutService } from '@navfleet/client/core/layout';
import { LayoutBreadcrumbService } from '@navfleet/client/core/layout';
import { FleetFilterService } from '@navfleet/client/fleet/domain/data-access';
import { FleetFacade } from '@navfleet/client/fleet/domain/data-access';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { LicenseUtil } from '@navfleet/client/license/util';
import { NavigationService } from '@navfleet/client/navigation/domain/data-access';
import { NAV_ROUTES, NavRoute } from '@navfleet/client/navigation/domain/models';
import {
    getNavRouteLicenses,
    getNavRoutePermissions,
    getRouteParam,
    getRouterUrl,
} from '@navfleet/client/navigation/utils';
import { PermissionFacade } from '@navfleet/client/permission/domain/data-access';
import { Permission } from '@navfleet/client/permission/domain/models';
import { hasRequiredPermissions } from '@navfleet/client/permission/util';
import { ToastComponent } from '@navfleet/client/shared/components/toast';
import { PARAM_VESSEL_ID } from '@navfleet/client/shared/constants/app';
import { UserSettings } from '@navfleet/client/shared/models/settings';
import { FullscreenService } from '@navfleet/client/shared/ui/fullscreen';
import { filterTruthy, shareLast, toArraySignal, toBooleanSignal } from '@navfleet/client/shared/util/functions';
import {
    SIDEMENU_ITEMS,
    SideMenuItem,
    USER_START_PAGE_ROUTES,
    UserStartPage,
} from '@navfleet/client/shell/domain/models';
import { LayoutUiComponent } from '@navfleet/client/shell/ui/layout-ui';
import { TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { UserFacade } from '@navfleet/client/user/domain/data-access';
import { UserPrivilegeFacade } from '@navfleet/client/user/privilege/domain/data-access';
import { Privilege } from '@navfleet/client/user/privilege/domain/models';
import { UserPrivilegeUtil } from '@navfleet/client/user/privilege/util';
import { UserSettingsFacade } from '@navfleet/client/user/settings/domain/data-access';
import { VesselFacade } from '@navfleet/client/vessel/domain/data-access';
import { combineLatest, combineLatestWith, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { LayoutNotificationsService } from './layout-notifications.service';

@Component({
    imports: [LayoutUiComponent, ToastComponent],
    selector: 'fl-layout-container',
    templateUrl: './layout.container.html',
    styleUrls: ['./layout.container.scss'],
})
export class LayoutContainer {
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly userPrivilegeFacade = inject(UserPrivilegeFacade);
    private readonly router = inject(Router);
    private readonly authenticationFacade = inject(AuthenticationFacade);
    private readonly userSettingsFacade = inject(UserSettingsFacade);
    private readonly authFacade = inject(AuthenticationFacade);
    private readonly userFacade = inject(UserFacade);
    private readonly navigationService = inject(NavigationService);
    private readonly destroyRef = inject(DestroyRef);
    public readonly layoutService = inject(LayoutService);
    public readonly consentService = inject(ConsentService);
    public readonly fleetFilterService = inject(FleetFilterService);
    public readonly layoutBreadcrumbService = inject(LayoutBreadcrumbService);
    public readonly vesselFacade = inject(VesselFacade);
    public readonly fleetFacade = inject(FleetFacade);
    public readonly fullscreenService = inject(FullscreenService);
    public readonly dateService = inject(DateService);
    public readonly layoutNotificationsService = inject(LayoutNotificationsService);
    public readonly tenantFacade = inject(TenantFacade);
    public readonly permissionFacade = inject(PermissionFacade);

    public readonly vesselId$ = getRouteParam(this.router, PARAM_VESSEL_ID);
    public readonly licenses$ = this.licenseFacade.licenses$.pipe(shareLast());
    public readonly currentUserRole$ = this.authFacade.userRole$;
    public readonly userCustomRoleName$ = this.authFacade.userCustomRoleName$;
    public readonly tenant$ = this.tenantFacade.getSelectedTenant();
    public readonly userCompanyName$ = combineLatest([
        this.authenticationFacade.userCustomCompanyName$,
        this.authenticationFacade.userCompanyName$,
    ]).pipe(map(([customCompanyName, companyName]) => customCompanyName ?? companyName));

    public readonly currentUser$ = this.authFacade.userId$.pipe(
        filterTruthy(),
        switchMap((id) => this.userFacade.getUserById(id)),
        filterTruthy()
    );

    public readonly sideMenuItems$ = combineLatest([
        this.licenses$,
        this.userPrivilegeFacade.userPrivileges$,
        this.permissionFacade.permissions$,
    ]).pipe(
        map(([licenses, privileges, permissions]) =>
            this.getAuthorizedMenuItems(SIDEMENU_ITEMS, licenses, privileges, permissions)
        )
    );

    private readonly firstRootRoute$ = combineLatest([this.licenses$, this.permissionFacade.permissions$]).pipe(
        map(
            ([licenses, permissions]) =>
                Object.values(NAV_ROUTES).find(
                    (route: NavRoute) =>
                        ![NAV_ROUTES.LOGIN, NAV_ROUTES.MANUAL, NAV_ROUTES.DASHBOARD]
                            .map<string>((r) => r.path)
                            .includes(route.path) &&
                        (!route.licenses || LicenseUtil.hasRequiredLicenses(route.licenses, licenses)) &&
                        hasRequiredPermissions(route.permissions, permissions)
                ) as NavRoute
        )
    );
    public readonly navFleetVessels = toArraySignal(this.vesselFacade.navFleetVessels$);
    public readonly isLoadingVessels = toBooleanSignal(this.vesselFacade.isInitialLoading$);
    public readonly fleets = toArraySignal(this.fleetFacade.fleets$);
    public readonly selectedFleetIds = toArraySignal(this.fleetFilterService.selectedFleetIds$);
    public readonly timezone = toSignal(this.dateService.timezone$);
    public readonly fleetNotifications = toArraySignal(this.layoutNotificationsService.fleetNotifications$);
    public readonly filteredOutNotificationCount = toSignal(
        this.layoutNotificationsService.filteredOutNotificationCount$
    );
    public readonly isSideMenuMinimized = toBooleanSignal(this.layoutService.isSideMenuMinimized$);
    public readonly isFullscreen = toBooleanSignal(this.fullscreenService.isFullscreen$);
    public readonly currentUser = toSignal(this.currentUser$);
    public readonly currentUserRole = toSignal(this.currentUserRole$);
    public readonly userCustomRoleName = toSignal(this.userCustomRoleName$);
    public readonly sideMenuItems = toArraySignal(this.sideMenuItems$);
    public readonly tenant = toSignal(this.tenant$);
    public readonly userCompanyName = toSignal(this.userCompanyName$);
    public readonly licenses = toArraySignal(this.licenses$);
    public readonly permissions = toArraySignal(this.permissionFacade.permissions$);
    public readonly vesselId = toSignal(this.vesselId$);

    constructor() {
        this.userSettingsFacade
            .getUserSetting(UserSettings.UserStartPage)
            .pipe(
                filterTruthy(),
                map((setting) => USER_START_PAGE_ROUTES[setting.value as UserStartPage]),
                take(1),
                switchMap((startPageRoute) =>
                    combineLatest([
                        of(startPageRoute),
                        this.licenseFacade.hasRequiredLicenses(getNavRouteLicenses(startPageRoute)),
                        this.permissionFacade.hasRequiredPermissions(getNavRoutePermissions(startPageRoute)),
                        this.licenseFacade.hasLicense(License.Viewer),
                    ])
                ),
                switchMap(([startPageRoute, hasLicenses, hasPermissions, isViewer]) =>
                    isViewer
                        ? of(NAV_ROUTES.VESSEL)
                        : hasLicenses && hasPermissions
                          ? of(startPageRoute)
                          : this.firstRootRoute$
                ),
                take(1),
                combineLatestWith(getRouterUrl(this.router)),
                filter(([, url]) => url === '/'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([resolvedRoute]) =>
                this.navigationService.navigate(resolvedRoute, undefined, { replaceUrl: true })
            );
    }

    public onVesselSelected(vesselId: string | undefined) {
        if (!vesselId) {
            return;
        }

        this.vesselId$
            .pipe(take(1))
            .subscribe((routeVesselId) =>
                routeVesselId && this.router.url.split('?')[0].includes(routeVesselId)
                    ? this.navigateReplaceVesselId(vesselId, routeVesselId)
                    : this.navigationService.navigate(NAV_ROUTES.VESSEL.children.DETAILS, vesselId)
            );
    }

    public onLogout() {
        this.authenticationFacade.logout();
    }

    private navigateReplaceVesselId(vesselId: string, routeVesselId: string): void {
        const path = this.router.url.split('?')[0];
        this.router.navigate([path.replace(routeVesselId, vesselId)]);
    }

    private getAuthorizedMenuItems(
        items: SideMenuItem[],
        licenses: License[],
        privileges: Privilege[],
        permissions: Permission[] | undefined
    ): SideMenuItem[] {
        return items
            .filter(
                (item) =>
                    LicenseUtil.hasRequiredLicenses(item.route.licenses ?? {}, licenses) &&
                    UserPrivilegeUtil.hasRequiredPrivileges(item.route.privileges ?? {}, privileges) &&
                    hasRequiredPermissions(item.route.permissions, permissions)
            )
            .map((item) =>
                item.children
                    ? {
                          ...item,
                          children: this.getAuthorizedMenuItems(item.children, licenses, privileges, permissions),
                      }
                    : item
            );
    }
}
